// @ts-nocheck — composed component; types added when touched again in a later sub-step
import React from "react";
const { useState, useEffect, useRef } = React;

/* composed/GrowthChart.tsx — 12-month overlay chart (Phase 4 sub-step 3).
   Extracted from src/legacy/app.tsx (was inline ~line 347). Pure render — no
   state mutations, no editing. Reads:
     - window.TOKENS for chart colors + spacing (mirrored by legacy primitives.tsx
       on every theme flip via MutationObserver)
     - window.MONTH_LABELS_IT / MONTH_LABELS_EN / MONTH_SHORT (legacy model.ts)
     - window.__fmtMoneyMrr (legacy MRR-pure formatter from model.ts)

   Theme reactivity: window.TOKENS is re-read by primitives.tsx on theme change;
   this chart consumes the live value at render time. The component also wires a
   `tokens-updated` listener so an in-place theme flip triggers a re-render
   without remounting.

   Props are passed by Dashboard and computed upstream (rows from
   computeForecast, todayMonthKey/Fraction from getTodayInfo, etc.). The
   chart itself does the SVG layout math but no business logic. */
function GrowthChart({ rows, target, lang, hiddenSeries, logScale, barMode, mode, expanded, lastLockedKey, todayMonthKey, todayMonthFraction, valueMode, yAxisLocked, fyMax, activeScenario, compareScenarios, chartScenarios }) {
  // Track container width with ResizeObserver so the SVG viewBox matches actual rendered width.
  // Without this, the viewBox is locked to 1440 and `preserveAspectRatio="xMidYMid meet"`
  // letterboxes the chart on wide screens. With dynamic width, the chart fills 100% horizontally.
  const wrapRef = React.useRef(null);
  const [containerW, setContainerW] = useState(1440);
  useEffect(() => {
    if (!wrapRef.current || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        const w = Math.max(600, Math.round(e.contentRect.width));
        setContainerW(w);
      }
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);
  const w = containerW, h = expanded ? 520 : 200, pad = { l: 110, r: 40, t: 36, b: expanded ? 64 : 36 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  if (rows.length === 0) return <div ref={wrapRef} className="chart-wrap"><svg viewBox={`0 0 ${w} ${h}`} className="chart" /></div>;

  // Stacked-bar segments per row (only for expanded view)
  const seriesDefs = [
    { key: "gtm",     label: lang === "it" ? "GTM" : "GTM",         color: "var(--lilac-700)",   getter: r => r.gtmTarget },
    { key: "ae",      label: "AE",                                  color: "var(--ae-500)",      getter: r => r.aeChannelGap },
    { key: "partner", label: "Partner",                             color: "var(--lilac-400)",   getter: r => r.partnerMrr },
    { key: "self",    label: "Self-Serve",                          color: "var(--lilac-300)",   getter: r => r.selfServeMrr },
    { key: "others",  label: "Others",                              color: "var(--lilac-200)",   getter: r => r.othersTotal },
    { key: "churn",   label: "Churn",                               color: "var(--danger-soft)", getter: r => -r.churnAbs }, // negative => below baseline
  ];

  // Y-axis behavior:
  //   - UNLOCKED (default): tight fit to *real* data + actually-rendered target trajectory line up to today.
  //     Future targets (which can rocket up to 250k) are excluded so early-year views aren't dwarfed.
  //   - LOCKED: include the full trajectory (future targets too) and FY target so axes stay
  //     stable when comparing across periods.
  const todayIdxForScale = rows.findIndex(r => r.key === todayMonthKey);
  const visibleRealMax = Math.max(0, ...rows.map(r => r.realEom || 0));
  // Past-or-current target values too (so target line stays in view through today)
  const visibleTargetThruTodayMax = Math.max(0, ...rows.map((r, i) =>
    todayIdxForScale < 0 || i <= todayIdxForScale ? (r.eomTarget || 0) : 0
  ));
  const unlockedMax = Math.max(1, visibleRealMax, visibleTargetThruTodayMax);
  const fullMax = Math.max(1, target, ...rows.map(r => Math.max(r.realEom || 0, r.eomTarget || 0)));
  const effectiveMax = yAxisLocked ? Math.max(fullMax, fyMax || 0) : unlockedMax;
  // niceCeil: round UP to a clean human number ("nice" ceiling) regardless of magnitude
  const niceCeil = (v) => {
    if (v <= 0) return 1000;
    const exp = Math.floor(Math.log10(v));
    const pow = Math.pow(10, exp);
    const norm = v / pow; // 1..10
    // Finer "nice" steps so values like 313 don't jump to 400 (snap to 320 instead)
    const stops = [1, 1.2, 1.25, 1.5, 1.6, 1.8, 2, 2.2, 2.5, 2.8, 3, 3.2, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 10];
    let nice = 10;
    for (const s of stops) {
      if (norm <= s) { nice = s; break; }
    }
    return nice * pow;
  };
  // Pad ~5% on top, then snap to nice number — gives a nicely-fitting axis
  const niceMax = niceCeil(effectiveMax * 1.05);
  // Apply log scale if requested
  const yScale = (v) => {
    if (!logScale) return pad.t + innerH * (1 - Math.max(0, v) / niceMax);
    const norm = Math.max(0, v) / niceMax;
    return pad.t + innerH * (1 - Math.sqrt(norm));
  };
  // For negative (churn) bars rendering, we keep them visible above x-axis as a small overlay band.
  const xStep = innerW / Math.max(1, rows.length);
  const xCenter = (i) => pad.l + xStep * (i + 0.5);
  // Wider bars when fewer rows (month/qtr views)
  const barWidth = rows.length <= 1 ? Math.min(180, xStep * 0.45)
                  : rows.length <= 3 ? Math.min(120, xStep * 0.55)
                  : Math.max(10, Math.min(64, xStep * 0.55));

  const realPts   = rows.map((r, i) => [xCenter(i), yScale(r.realEom || 0)]);
  const targetPts = rows.map((r, i) => [xCenter(i), yScale(r.eomTarget || 0)]);
  const realMask  = rows.map(r => r.realEom != null && (r.realEom > 0 || r.locked));
  // Find "today" index in rows (last index with real value, or first non-locked)
  const todayIdx = (() => {
    if (todayMonthKey) {
      const i = rows.findIndex(r => r.key === todayMonthKey);
      if (i >= 0) return i;
    }
    // fallback: last row that has a real value
    for (let i = realMask.length - 1; i >= 0; i--) if (realMask[i]) return i;
    return -1;
  })();
  const smooth = (arr) => {
    if (arr.length < 2) return "";
    let d = `M ${arr[0][0]},${arr[0][1]}`;
    for (let i = 0; i < arr.length - 1; i++) {
      const p0 = arr[i - 1] || arr[i], p1 = arr[i], p2 = arr[i + 1], p3 = arr[i + 2] || p2;
      const tt = 0.18;
      d += ` C ${p1[0]+(p2[0]-p0[0])*tt},${p1[1]+(p2[1]-p0[1])*tt} ${p2[0]-(p3[0]-p1[0])*tt},${p2[1]-(p3[1]-p1[1])*tt} ${p2[0]},${p2[1]}`;
    }
    return d;
  };
  const targetPath = smooth(targetPts);
  // Real path: only segments where real is known (solid), then projection from last real to target endpoint (dashed)
  const realSegments = [];
  let cur = [];
  for (let i = 0; i < realPts.length; i++) {
    if (realMask[i]) cur.push(realPts[i]);
    else { if (cur.length >= 2) realSegments.push(cur); cur = []; }
  }
  if (cur.length >= 2) realSegments.push(cur);

  // Projection: from last real point to last target point (linear interp through future targets for shape)
  // Strategy: collect future points starting at lastReal (anchor) then for each future row, use the target value (so line catches up to target by end)
  const lastRealIdx = (() => {
    for (let i = realMask.length - 1; i >= 0; i--) if (realMask[i]) return i;
    return -1;
  })();
  let projPath = "";
  let projPts = [];
  if (lastRealIdx >= 0 && lastRealIdx < rows.length - 1) {
    projPts.push(realPts[lastRealIdx]);
    // For interim months, blend: t goes 0→1 from lastRealIdx → endIdx; y interpolates from lastReal value to target at end
    const endIdx = rows.length - 1;
    const lastRealY = realPts[lastRealIdx][1];
    const endTargetY = targetPts[endIdx][1];
    for (let i = lastRealIdx + 1; i <= endIdx; i++) {
      const t = (i - lastRealIdx) / (endIdx - lastRealIdx);
      // ease toward target with a slight curve (sqrt for catch-up feel)
      const eased = Math.pow(t, 0.85);
      const y = lastRealY + (endTargetY - lastRealY) * eased;
      projPts.push([xCenter(i), y]);
    }
    projPath = smooth(projPts);
  }

  // Area fill between Real and Target (green if real > target, red if real < target)
  // Use full timeline: real (with projection extension) vs target. Build polygons month-by-month.
  const realTimeline = []; // y-values aligned with target indices
  for (let i = 0; i < rows.length; i++) {
    if (realMask[i]) realTimeline.push(realPts[i][1]);
    else if (i > lastRealIdx && lastRealIdx >= 0) {
      // use projection value
      const projOffset = i - lastRealIdx;
      const pp = projPts[projOffset];
      if (pp) realTimeline.push(pp[1]); else realTimeline.push(null);
    } else realTimeline.push(null);
  }

  // Build per-segment area paths between real & target where both defined — ONLY up to today
  const areaSegments = []; // {path, sign} sign>0 means real above target (good = lower y)
  {
    let segStart = -1;
    let segSign = null;
    const lastAreaIdx = todayIdx >= 0 ? todayIdx : (lastRealIdx >= 0 ? lastRealIdx : -1);
    const flush = (endI) => {
      if (segStart < 0 || endI <= segStart) return;
      // y is inverted: lower y = higher value. real ABOVE target visually = realY < targetY
      const pieces = [];
      // top edge: real from segStart..endI
      for (let i = segStart; i <= endI; i++) pieces.push([xCenter(i), realTimeline[i]]);
      // bottom edge back: target from endI..segStart
      for (let i = endI; i >= segStart; i--) pieces.push([xCenter(i), targetPts[i][1]]);
      let d = `M ${pieces[0][0]},${pieces[0][1]}`;
      for (let i = 1; i < pieces.length; i++) d += ` L ${pieces[i][0]},${pieces[i][1]}`;
      d += " Z";
      areaSegments.push({ d, sign: segSign });
    };
    const stopIdx = lastAreaIdx >= 0 ? Math.min(rows.length - 1, lastAreaIdx) : -1;
    for (let i = 0; i <= stopIdx; i++) {
      if (realTimeline[i] == null) { flush(i - 1); segStart = -1; segSign = null; continue; }
      const sign = realTimeline[i] <= targetPts[i][1] ? 1 : -1; // 1 = real >= target (good)
      if (segStart < 0) { segStart = i; segSign = sign; }
      else if (sign !== segSign) {
        flush(i - 1);
        segStart = i; segSign = sign;
      }
      if (i === stopIdx) flush(i);
    }
  }

  const ticks = []; const tickCount = expanded ? 5 : 3;
  for (let i = 0; i <= tickCount; i++) ticks.push((niceMax * i) / tickCount);
  const mul = valueMode === "arr" ? 12 : 1;
  const fmtTick = (v) => window.__fmtMoneyMrr ? window.__fmtMoneyMrr(v * mul) : ("€" + Math.round(v * mul));

  return (
    <div ref={wrapRef} className="chart-wrap">
    <svg viewBox={`0 0 ${w} ${h}`} className="chart" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="areaGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--lilac-500)" stopOpacity="0.32" />
          <stop offset="100%" stopColor="var(--lilac-500)" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="areaGood" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--good)" stopOpacity="0.30" />
          <stop offset="100%" stopColor="var(--good)" stopOpacity="0.05" />
        </linearGradient>
        <linearGradient id="areaBad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--warn)" stopOpacity="0.05" />
          <stop offset="100%" stopColor="var(--warn)" stopOpacity="0.30" />
        </linearGradient>
      </defs>
      <rect x={pad.l} y={pad.t} width={innerW} height={innerH} className="chart__bg" />
      {/* Past zone (before today) shaded subtle gray to visually separate "happened" vs "forecast" */}
      {(() => {
        const idx = rows.findIndex(r => r.key === todayMonthKey);
        if (idx < 0) return null;
        const xToday = xCenter(idx);
        const widthPast = xToday - pad.l;
        if (widthPast <= 0) return null;
        return <rect className="chart__past-zone" x={pad.l} y={pad.t} width={widthPast} height={innerH} />;
      })()}
      {/* y-grid */}
      {ticks.map((tv, i) => (
        <g key={i}>
          <line
            x1={pad.l} x2={pad.l + innerW} y1={yScale(tv)} y2={yScale(tv)}
            stroke={tv === 0 ? "var(--ink-2)" : "var(--line)"}
            strokeWidth={tv === 0 ? 1.4 : 1}
            strokeDasharray={tv === 0 ? "0" : "2 4"}
            opacity={tv === 0 ? 0.55 : 1}
          />
          <text x={pad.l - 12} y={yScale(tv) + 4} textAnchor="end" className="chart__tick" style={tv === 0 ? {fontWeight: 700, fill: "var(--ink-1)"} : null}>{fmtTick(tv)}</text>
        </g>
      ))}

      {/* Stacked or grouped bars per month */}
      {expanded && rows.map((r, i) => {
        const x = xCenter(i) - barWidth / 2;
        // Active series defs (hidden ones excluded)
        const activeSegs = seriesDefs
          .filter(d => d.key !== "churn" && !hiddenSeries[d.key])
          .map(d => ({ key: d.key, color: d.color, v: Math.max(0, d.getter(r)) }))
          .filter(s => s.v > 0);

        let segs = [];
        if (barMode === "grouped") {
          // Side-by-side mini bars
          const n = activeSegs.length;
          if (n > 0) {
            const groupW = barWidth;
            const subW = Math.max(3, (groupW - (n - 1) * 2) / n);
            activeSegs.forEach((s, k) => {
              const barH = (s.v / niceMax) * innerH;
              const sx = x + k * (subW + 2);
              const sy = pad.t + innerH - barH;
              segs.push({ ...s, x: sx, y: sy, w: subW, h: barH });
            });
          }
        } else {
          // Stacked
          let stackBase = pad.t + innerH;
          activeSegs.forEach(s => {
            const barH = (s.v / niceMax) * innerH;
            stackBase -= barH;
            segs.push({ ...s, x, y: stackBase, w: barWidth, h: barH });
          });
        }

        return (
          <g key={r.key}>
            {segs.map(s => (
              <rect key={s.key} x={s.x} y={s.y} width={s.w} height={s.h} fill={s.color} opacity={r.locked ? 0.85 : 0.6}>
                <title>{r.key.toUpperCase()} · {s.key}: €{Math.round(s.v).toLocaleString("de-DE")}</title>
              </rect>
            ))}
            {/* Negative churn segment below stack — smaller in grouped mode */}
            {!hiddenSeries.churn && r.churnAbs > 0 && (() => {
              const isGrouped = barMode === "grouped";
              const cBarW = isGrouped ? Math.max(4, barWidth * 0.35) : barWidth;
              const cBarX = isGrouped ? xCenter(i) - cBarW / 2 : x;
              const barH = Math.min(innerH * 0.15, (r.churnAbs / niceMax) * innerH);
              const y = pad.t + innerH;
              return (
                <rect x={cBarX} y={y} width={cBarW} height={barH} fill="var(--danger-soft)" opacity={0.5}>
                  <title>Churn: €{Math.round(r.churnAbs).toLocaleString("de-DE")}</title>
                </rect>
              );
            })()}
          </g>
        );
      })}

      {/* Today line */}
      {(() => {
        const idx = rows.findIndex(r => r.key === todayMonthKey);
        if (idx < 0) return null;
        const x = xCenter(idx);
        return (
          <g>
            <line x1={x} x2={x} y1={pad.t} y2={pad.t + innerH} stroke="var(--lilac-700)" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.7" />
            <rect x={x - 26} y={pad.t - 19} width={52} height={16} rx={8} className="chart__today-tag-bg" />
            <text x={x} y={pad.t - 8} textAnchor="middle" className="chart__today-tag">{lang === "it" ? "oggi" : "today"}</text>
          </g>
        );
      })()}

      {/* Area fill between Real and Target */}
      {!hiddenSeries.realLine && !hiddenSeries.targetLine && areaSegments.map((seg, i) => (
        <path key={`area-${i}`} d={seg.d} fill={seg.sign > 0 ? "url(#areaGood)" : "url(#areaBad)"} stroke="none" />
      ))}

      {/* Scenario lines: when nothing is selected (chartScenarios empty) → show all 3 SOLID for compare.
          When one or more are selected → show ONLY those, solid bold; the unselected become hidden. */}
      {compareScenarios && !hiddenSeries.targetLine && (() => {
        const scnSelected = chartScenarios && chartScenarios.length > 0;
        const order = ["pessimistic", "base", "optimistic"];
        return order.map(scn => {
          const scnRows = compareScenarios[scn];
          if (!scnRows) return null;
          const visible = !scnSelected || chartScenarios.includes(scn);
          if (!visible) return null;
          const pts = scnRows.map((r, i) => [xCenter(i), yScale(r.eomTarget)]);
          const stroke = scn === "optimistic" ? "var(--good)" : scn === "pessimistic" ? "var(--warn)" : "var(--lilac-700)";
          // When selected → bold solid emphasis. When in "show all" → solid normal.
          const isEmphasized = scnSelected && chartScenarios.includes(scn);
          const strokeWidth = isEmphasized ? 2.8 : 2.0;
          return <path key={`scn-${scn}`} d={smooth(pts)} fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" />;
        });
      })()}
      {/* Real line — solid up to today */}
      {!hiddenSeries.realLine && realSegments.map((seg, i) => (
        <path key={i} d={smooth(seg)} fill="none" stroke="var(--proj)" strokeWidth="3" strokeLinecap="round" />
      ))}
      {/* Flat-baseline reference: horizontal line from today's real value across the rest of the chart.
          Shows "where we'd land if MRR plateaus from today onward" — a visual anchor that contrasts
          the dashed forecast trajectory above it. */}
      {!hiddenSeries.realLine && lastRealIdx >= 0 && lastRealIdx < rows.length - 1 && (() => {
        const x0 = realPts[lastRealIdx][0];
        const y0 = realPts[lastRealIdx][1];
        const x1 = xCenter(rows.length - 1);
        return (
          <g>
            <line x1={x0} x2={x1} y1={y0} y2={y0}
              stroke="var(--proj)" strokeWidth="1.4" strokeDasharray="1 4"
              opacity="0.45" strokeLinecap="round" />
            {expanded && (
              <text x={x1 - 4} y={y0 - 6} textAnchor="end"
                fontSize={window.TOKENS.fs["body-xs"]} fill="var(--proj)" opacity="0.7" fontFamily="Geist Mono, monospace">
                {lang === "it" ? "se piatto" : "if flat"}
              </text>
            )}
          </g>
        );
      })()}
      {/* Projection: from last real point to target endpoint (dotted, lighter) */}
      {!hiddenSeries.realLine && projPath && (
        <g>
          <path d={projPath} fill="none" stroke="var(--proj)" strokeWidth="2.4" strokeDasharray="6 6" strokeLinecap="round" opacity="0.9" />
          {expanded && projPts.length > 1 && (() => {
            // Label the projection line with "forecast" near its midpoint
            const mid = projPts[Math.floor(projPts.length / 2)];
            return (
              <text x={mid[0]} y={mid[1] - 8} textAnchor="middle"
                fontSize={window.TOKENS.fs["body-xs"]} fill="var(--proj)" opacity="0.85" fontFamily="Geist Mono, monospace" fontWeight="600">
                {lang === "it" ? "forecast" : "forecast"}
              </text>
            );
          })()}
        </g>
      )}
      {/* Real dots + per-month perf badge (real vs target ±%).
          For the CURRENT month we prorate the target by day-of-month so the % reflects
          where we should be by today, not vs the full-month target. */}
      {!hiddenSeries.realLine && rows.map((r, i) => {
        if (!realMask[i]) return null;
        const cx = xCenter(i);
        const cy = yScale(r.realEom);
        let tgt = r.eomTarget || 0;
        if (r.key === todayMonthKey && typeof todayMonthFraction === "number" && tgt > 0) {
          const prevR = i > 0 ? rows[i - 1] : null;
          const prevEom = prevR ? (prevR.eomTarget || 0) : 0;
          tgt = prevEom + (tgt - prevEom) * todayMonthFraction;
        }
        const pct = tgt > 0 ? ((r.realEom - tgt) / tgt) * 100 : 0;
        const sign = pct >= 0 ? "+" : "";
        const tone = pct >= -3 ? "good" : pct >= -10 ? "ok" : "bad";
        const badgeColor = tone === "good" ? "var(--good)" : tone === "ok" ? "var(--warn-mid)" : "var(--warn)";
        const badgeText = `${sign}${pct.toFixed(0)}%`;
        // Position badge above the dot, but flip below if too close to top
        const above = cy - pad.t > 38;
        const by = above ? cy - 18 : cy + 24;
        const labelW = badgeText.length * 8 + 14;
        return (
          <g key={r.key}>
            <circle cx={cx} cy={cy} r={5} fill="var(--proj)" stroke="white" strokeWidth="1.5"
              vectorEffect="non-scaling-stroke" shapeRendering="geometricPrecision" />
            {expanded && tgt > 0 && (
              <g>
                <rect x={cx - labelW/2} y={by - 11} width={labelW} height={20} rx={10} className="chart__bg" stroke={badgeColor} strokeWidth="1.2" />
                <text x={cx} y={by + 4} textAnchor="middle" fontSize={window.TOKENS.fs.body} fontWeight="500" fill={badgeColor} fontFamily="Geist Mono, monospace">{badgeText}</text>
              </g>
            )}
          </g>
        );
      })}

      {/* Target endpoint marker */}
      {!hiddenSeries.targetLine && (() => {
        const last = targetPts[targetPts.length - 1];
        return <circle cx={last[0]} cy={last[1]} r={6} fill="var(--lilac-700)" />;
      })()}

      {/* x-axis labels */}
      {rows.map((r, i) => (
        <text key={r.key} x={xCenter(i)} y={pad.t + innerH + 28} textAnchor="middle"
          className={`chart__xlabel ${r.key === todayMonthKey ? "chart__xlabel--today" : ""}`}>
          {(mode === "month" || mode === "qtr")
            ? (lang === "it" ? window.MONTH_LABELS_IT[r.key] : window.MONTH_LABELS_EN[r.key])
            : window.MONTH_SHORT[r.key]}
        </text>
      ))}
    </svg>
    </div>
  );
}

export default GrowthChart;
